import { Button } from "components/Button/Button";
import { FormComponentProps } from "components/Form/components/FormComponentProps";
import { getUpdatedData } from "components/Form/components/SchemaForm/SchemaFormService";
import { Toggle } from "components/Form/components/Toggle/Toggle";
import { LazyResizeableCodeEditor as ResizeableCodeEditor } from "components/Form/lazyCodeEditorComponents";
import { IconCollection } from "components/Icon/IconCollection";
import Tooltip from "components/Tooltip/Tooltip";
import { FilterFragment } from "containers/Filter/FilterBuilder/components/FilterFragment";
import { emptyExists, emptyField } from "containers/Filter/FilterBuilder/constants";
import { getObjectTemplateAttributeFields } from "containers/Filter/FilterBuilder/objectTemplateFilterFields";
import { findFilteredObjectTemplateId } from "containers/Filter/FilterBuilder/selectedObjectTemplate";
import { useMatchingFields } from "containers/Filter/FilterBuilder/useMatchingFields";
import { useMatchingRelations } from "containers/Filter/FilterBuilder/useMatchingRelations";
import { objectTemplateSchemaSelector } from "containers/Resources/Resources.selectors";
import { isEqual } from "lodash";
import { useMemo, useState } from "react";
import { Form as FinalForm, useFormState } from "react-final-form";
import { useSelector } from "react-redux";
import { isRecordFilterGroup, isRecordFilterLeaf } from "utils/recordFilter";
import "./RecordFilterBuilderFormComponent.scss";

/**
 * Filter-builder editor for a PLUGIN_ACTION `record_filter`.
 *
 * Renders the same Advanced-Search filter builder form controls (FilterFragment ->
 * FilterGroup -> FilterField / ValueField / ExistsFilterField) that the advanced-search
 * screen uses, instead of a raw Monaco YAML editor. The value shape it emits/consumes is
 * the record_filter grammar itself — `{and|or:[{field, op, value} | nested group]}` — so
 * no (de)serialisation is needed between the control and the stored `SchemalessData`
 * (the same grammar the Part-A client evaluator `matchesRecordFilter` runs to decide
 * button visibility, src/utils/recordFilter.ts).
 *
 * Field-list scoping mirrors AdvancedSearch:
 *  - the sibling `record_type` field (read live via useFormState) picks the schema table
 *    whose columns become filterable fields (`activeTab = record_type.toLowerCase()`);
 *  - for `custom_object` actions, once the filter itself pins one object template with an
 *    `object_template_id eq <id>` leaf, that template's attributes are exposed as synthetic
 *    `value.<attr>` fields (getObjectTemplateAttributeFields) — exactly how the operator-task
 *    buttons filter on `value.status` / `value.assignee` / `value.result_unread`.
 *
 * Legacy handling: older stored filters may be a flat `{key: value}` map (pre-grammar). The
 * builder can only render the `{and|or}` grammar, so such a value is normalised to
 * `{and:[{field:key, op:'eq', value}]}` for DISPLAY only — no onChange is emitted on load, so
 * merely opening the form never rewrites the stored value; the grammar is written on the first
 * user edit (and the backend data-migration converts stored legacy filters to the grammar too).
 *
 * `exists` / `not.exists` (has-related-record) nodes ARE editable here, but the client-side
 * button-visibility evaluator resolves them to non-matching (a DB round-trip would be needed),
 * so a plugin-action button gated on an `exists` filter simply never shows.
 */
/**
 * Normalise a stored record_filter value to the `{and|or}` grammar the FilterBuilder
 * renders, for DISPLAY only (the caller never emits this on load — see the component doc).
 *  - a boolean-group grammar tree (`{and|or:[...]}`) passes through unchanged;
 *  - a bare leaf comparison `{field, op, value}` is wrapped as a single-condition AND (one row);
 *  - a legacy flat `{key: value}` map becomes an equivalent AND-of-eq tree;
 *  - an empty / null / non-object value returns undefined (renders the "Add filter" empty state).
 * Pure and side-effect-free so it can be unit-tested without a browser.
 */
export const toDisplayGrammar = (value: unknown): any => {
    // Already a boolean-group grammar tree — renders as-is.
    if (isRecordFilterGroup(value)) {
        return value;
    }
    // A bare leaf comparison `{field, op, value}` — a single condition NOT wrapped in an and/or
    // group. Wrap it as a one-condition AND so the builder renders ONE row, instead of falling
    // through to the legacy-flat branch below (which would iterate its own `field`/`op`/`value`
    // keys into three bogus rows — the reported bug).
    if (isRecordFilterLeaf(value)) {
        return { and: [value] };
    }
    if (
        value &&
        typeof value === "object" &&
        !Array.isArray(value) &&
        Object.keys(value as object).length > 0
    ) {
        return {
            and: Object.entries(value as Record<string, unknown>).map(([field, entryValue]) => ({
                field,
                op: "eq",
                value: entryValue,
            })),
        };
    }
    return undefined;
};

/**
 * Serialise a stored record_filter value to the pretty JSON text shown in the code view.
 * An empty / null value serialises to the empty string (the "no filter" state, which the
 * code view parses back to `undefined`). Pure and side-effect-free for unit testing.
 */
export const serializeRecordFilter = (value: unknown): string => {
    if (value === undefined || value === null) {
        return "";
    }
    try {
        return JSON.stringify(value, null, 2);
    } catch {
        return "";
    }
};

export type ParsedRecordFilter =
    | { ok: true; value: unknown }
    | { ok: false; error: string };

/**
 * Parse the code-view JSON text back into a record_filter value.
 *  - empty / whitespace-only text clears the filter (`undefined`);
 *  - malformed JSON is reported as an error (never written to the stored value);
 *  - a parsed non-object (number/string/array/null) is rejected — a record_filter is a
 *    comparison `{field, op, value}` or a boolean `{and|or:[…]}` object (shape beyond that is
 *    left to the schemaless field + the server, exactly as the form builder does).
 * Pure and side-effect-free for unit testing.
 */
export const parseRecordFilterCode = (text: string): ParsedRecordFilter => {
    const trimmed = text.trim();
    if (trimmed === "") {
        return { ok: true, value: undefined };
    }
    let parsed: unknown;
    try {
        parsed = JSON.parse(trimmed);
    } catch (error: any) {
        return { ok: false, error: `Invalid JSON: ${error?.message ?? "could not parse"}` };
    }
    if (parsed === null || typeof parsed !== "object" || Array.isArray(parsed)) {
        return {
            ok: false,
            error: 'A record filter must be a JSON object, e.g. {"field": "status", "op": "eq", "value": "active"} or {"and": […]}.',
        };
    }
    return { ok: true, value: parsed };
};

export interface RecordFilterBuilderProps {
    /** Field name; used for the code-editor input name and the test id. */
    name: string;
    /** The stored filter value (grammar object, leaf, legacy flat map, or empty). */
    value: unknown;
    /** Called with the new filter value (or `undefined` to clear it). */
    onChange: (value: unknown) => void;
    /** Record type whose columns become filterable fields, e.g. `CUSTOM_OBJECT`. */
    recordType?: string;
    /** Label text shown above the control. Omit when the caller renders its own label. */
    label?: string;
    /** Shown instead of the builder while no record type is chosen. */
    missingRecordTypeNote?: string;
    hasError?: boolean;
    errorText?: string;
}

/**
 * The filter builder itself, independent of how the surrounding form stores its values.
 * Used by the plugin-action form (react-final-form, via RecordFilterBuilderFormComponent)
 * and by the schema-form `form-record-filter` element (dashboard widget editor).
 */
export const RecordFilterBuilder = ({
    name,
    value,
    onChange,
    recordType,
    label,
    missingRecordTypeNote = "Choose a record type first, then build the filter.",
    hasError = false,
    errorText,
}: RecordFilterBuilderProps) => {
    const activeTab = recordType ? String(recordType).toLowerCase() : "";
    const input = { name, value, onChange };

    // Normalise the stored value to the grammar the builder renders. Grammar passes through;
    // a legacy flat map is shown as an equivalent AND-of-eq tree (display only — see doc note);
    // anything else (empty / null) shows the empty "Add filter" state.
    const displayFilter = useMemo<any>(() => toDisplayGrammar(input.value), [input.value]);

    // value.<attr> fields for a custom-object action, scoped to the template the filter pins.
    const objectTemplateSchemaList: any[] = useSelector(objectTemplateSchemaSelector, isEqual);
    const selectedObjectTemplateId = useMemo(
        () => findFilteredObjectTemplateId(displayFilter),
        [displayFilter]
    );
    const attributeFields = useMemo(() => {
        if (activeTab !== "custom_object" || !selectedObjectTemplateId) {
            return undefined;
        }
        const template = objectTemplateSchemaList.find(
            (entry) => entry?.id === selectedObjectTemplateId
        );
        return template ? getObjectTemplateAttributeFields(template.schema) : undefined;
    }, [activeTab, selectedObjectTemplateId, objectTemplateSchemaList]);

    const { matchingFields, filteredFieldList } = useMatchingFields({ activeTab, attributeFields });
    const relationOptions = useMatchingRelations({ activeTab });

    // Form-view (the filter builder) <-> code-view (raw JSON) toggle. `formView` true = builder.
    // `codeText` is the editor's working text, seeded from the stored value whenever the code
    // view is entered so partial/invalid keystrokes never stomp the stored value. The stored
    // record_filter is only written (input.onChange) when the code parses to a valid object.
    const [formView, setFormView] = useState(true);
    const [codeText, setCodeText] = useState<string>(() => serializeRecordFilter(input.value));
    const [codeError, setCodeError] = useState<string | undefined>(undefined);
    const isCodeValid = !codeError;

    // Parse the current code text and, if valid, write it back to the stored value.
    // Returns whether the text was valid (used to gate the switch back to form view).
    const applyCodeText = (text: string): boolean => {
        const parsed = parseRecordFilterCode(text);
        if (parsed.ok) {
            setCodeError(undefined);
            input.onChange(parsed.value);
            return true;
        }
        setCodeError(parsed.error);
        return false;
    };

    const onToggleView = (_: unknown, selected: string | number | boolean | null) => {
        // ToggleButtonGroup is exclusive: a click on the already-selected button yields null.
        if (selected === null) {
            return;
        }
        if (selected) {
            // Switching to form view: commit any pending code edits first; stay in code view
            // (so the user can fix it) if the JSON is invalid.
            if (!applyCodeText(codeText)) {
                return;
            }
            setFormView(true);
        } else {
            // Entering code view: re-seed the editor from the current stored value.
            setCodeText(serializeRecordFilter(input.value));
            setCodeError(undefined);
            setFormView(false);
        }
    };

    const renderViewToggle = () => {
        // While the code is invalid the toggle is locked to code view (you can't carry a broken
        // filter into the form builder) — mirrors the schema-form code/form toggle behaviour.
        const toggle = (
            <Toggle
                identifier="recordFilterViewMode"
                options={[
                    { value: true, label: "Form view", icon: IconCollection.formTextbox },
                    { value: false, label: "Code view", icon: IconCollection.codeJson },
                ]}
                value={formView}
                onChange={onToggleView}
                attributes={{ disabled: !isCodeValid, size: "small" }}
            />
        );
        if (!isCodeValid) {
            return (
                <Tooltip overlay="The code view contains invalid JSON. Fix it to switch back to the form view.">
                    {toggle}
                </Tooltip>
            );
        }
        return toggle;
    };

    const renderCodeView = () => (
        <div className="recordFilterBuilder__codeView">
            <ResizeableCodeEditor
                input={{
                    name: `${input.name}__code`,
                    value: codeText,
                    onChange: (value: string) => setCodeText(value ?? ""),
                    onBlur: () => applyCodeText(codeText),
                    onFocus: () => {},
                }}
                fieldAttr={{}}
                meta={{ error: codeError, touched: Boolean(codeError) }}
                fieldProps={{ lang: "json" }}
            />
        </div>
    );

    const renderControl = () => {
        // A record type must be chosen first — the field list has nothing to scope to otherwise.
        if (!activeTab) {
            return (
                <div className="recordFilterBuilder__note recordFilterBuilder__note--muted">
                    {missingRecordTypeNote}
                </div>
            );
        }
        // Isolate the filter-builder's nested react-final-form <Field> controls in their
        // OWN form (seeded from the current record_filter grammar), exactly as the
        // advanced-search CustomFilterField does. Without this wrapper the leaf field/value
        // <Field>s register into the PARENT plugin_action form at root-level paths
        // ("[0].field", "[0].value", …) that never match the record_filter-NESTED data, so
        // their required-validators fire "Required." into the parent form and permanently
        // disable its Save button (a populated filter still reads as empty from the parent's
        // view). Nesting both (a) isolates those validators from the parent form's validity
        // and (b) binds them to the real filter values so a populated filter validates clean.
        // Edits still bridge back to the parent record_filter via setData={input.onChange}.
        return (
            <FinalForm onSubmit={() => {}} initialValues={displayFilter}>
                {({ values: nestedValues }) =>
                    nestedValues && Object.values(nestedValues)[0] ? (
                        <FilterFragment
                            parentData={nestedValues}
                            formData={nestedValues}
                            matchingFields={matchingFields}
                            filteredFieldList={filteredFieldList}
                            parentPath=""
                            isRoot={true}
                            setData={input.onChange}
                            activeTab={activeTab}
                        />
                    ) : (
                        <div className="addButtonContainer">
                            <Button
                                onClick={() =>
                                    input.onChange(
                                        getUpdatedData(nestedValues, "", { and: [emptyField] })
                                    )
                                }
                                variation="white"
                                label="Add filter"
                            />
                            {relationOptions.length > 0 && (
                                <Button
                                    onClick={() =>
                                        input.onChange(
                                            getUpdatedData(nestedValues, "", { and: [emptyExists] })
                                        )
                                    }
                                    variation="white"
                                    label="Has related record"
                                />
                            )}
                        </div>
                    )
                }
            </FinalForm>
        );
    };

    return (
        <div
            className={`recordFilterBuilder${hasError ? " recordFilterBuilder--error" : ""}`}
            data-testid={input.name}
        >
            <div className="recordFilterBuilder__label">
                {label && <span className="recordFilterBuilder__labelText">{label}</span>}
                <div className="recordFilterBuilder__viewToggle">{renderViewToggle()}</div>
            </div>
            <div className="recordFilterBuilder__control execution-search-filters advanced-search-filters">
                {formView ? renderControl() : renderCodeView()}
            </div>
            {hasError && errorText && <div className="recordFilterBuilder__errorText">{errorText}</div>}
        </div>
    );
};

/** react-final-form adapter (plugin-action form): reads the sibling `record_type` live. */
export const RecordFilterBuilderFormComponent = ({ input, fieldProps, meta }: FormComponentProps) => {
    const { values } = useFormState({ subscription: { values: true } });
    const hasError = Boolean((fieldProps?.validateOnMount || meta?.touched) && meta?.error);
    return (
        <RecordFilterBuilder
            name={input.name}
            value={input.value}
            onChange={input.onChange}
            recordType={values?.record_type}
            label={fieldProps?.label ?? "Record filter"}
            missingRecordTypeNote="Choose a record type first, then build the filter that decides when this action is shown."
            hasError={hasError}
            errorText={meta?.error}
        />
    );
};
